import React, { useMemo, useState } from "react";
import Capabilities from "../Capabilities";
import AutocompleteTagInput from "../AutocompleteTagInput";
import ServiceCategoryV2Dropdown from "../ServiceCategoryV2Dropdown";
import { dateFormat } from "../../utils/helperMethods";
import {
  ADDITIONAL_SCOPE_CHIP_CLASS,
  parseDelimitedScopeTags,
} from "../../utils/scopeDisplayUtils";

const OUTLINE_BUTTON_CLASS =
  "inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50";
const CONFIRM_BUTTON_CLASS = `${OUTLINE_BUTTON_CLASS} border-amber-600 text-amber-700 hover:bg-amber-100`;
const SAVE_BUTTON_CLASS = `${OUTLINE_BUTTON_CLASS} border-indigo-600 text-indigo-700 hover:bg-indigo-50`;

// Callers render this only when there is at least one service or tag.
function AddOnBadges({ services, tags }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {services.length > 0 && <Capabilities services={services} icon={true} />}
      {tags.map((tag, index) => (
        <span key={`${tag}-${index}`} className={ADDITIONAL_SCOPE_CHIP_CLASS}>
          {tag}
        </span>
      ))}
    </div>
  );
}

function formatJoinRequestContact(contact) {
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.email;
  return contact.job_title ? `${name} (${contact.job_title})` : name;
}

/**
 * What this joining sponsor submitted with its Request to Join (draft-date ask, vendor
 * request contacts, comments). Lives on the sponsor's own Details overview, not the grid.
 */
function JoinRequestDetails({ joinDetails }) {
  const contacts = joinDetails.request_contacts ?? [];
  const rows = [
    joinDetails.desired_initial_draft_date && {
      label: "Desired Initial Draft Report Date",
      value: dateFormat(joinDetails.desired_initial_draft_date),
    },
    contacts.length > 0 && {
      label: "Request Contact(s)",
      value: contacts.map(formatJoinRequestContact).join(", "),
    },
    joinDetails.additional_info && {
      label: "Additional Information",
      value: joinDetails.additional_info,
    },
  ].filter(Boolean);
  if (rows.length === 0) return null;

  return (
    <div className="mt-4">
      <h3 className="text-sm font-medium text-gray-900">Join Request Details:</h3>
      <dl className="mt-1 space-y-2">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-xs font-medium text-gray-500">{row.label}</dt>
            <dd className="whitespace-pre-wrap break-words text-sm text-gray-900">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Renders the sponsor-scoped Assessment Details "Scope Add-ons" area for a join
 * sub-request. Never renders anything for the originator
 * instance — the caller only passes `joinDetails` when the URL resolved to an
 * actual join row (`?sponsor=&status=`).
 *
 * `ConfirmAddOnScope`/`UpdateJoinVQAScope` are separate BE endpoints from the main
 * VQA details save, so this section owns its own Save action in edit mode rather
 * than piggy-backing on the parent form's global Save button.
 */
export default function JoinScopeAddOnsSection({
  joinDetails,
  isPending = false,
  isEditMode = false,
  onConfirm = () => {},
  isConfirming = false,
  onSaveScope = () => {},
  isSavingScope = false,
  rfiList = [],
  scopeSuggestions = [],
}) {
  const addOnServices = (joinDetails?.scope_add_ons ?? []).filter(
    (s) => s?.status !== "Cancelled"
  );
  const pendingAddOnServices = addOnServices.filter(
    (s) => s?.status === "Pending"
  );
  const confirmedAddOnServices = addOnServices.filter(
    (s) => s?.status === "Approved"
  );
  const addOnTags = useMemo(
    () => parseDelimitedScopeTags(joinDetails?.addon_scope_tags),
    [joinDetails?.addon_scope_tags]
  );
  const sponsorAdditionalTags = useMemo(
    () => parseDelimitedScopeTags(joinDetails?.sponsor_additional_scope_tags),
    [joinDetails?.sponsor_additional_scope_tags]
  );

  // Baseline = this join's `capabilities` minus its own add-on services (the DTO's
  // `capabilities` field keeps its legacy combined shape — baseline + own add-ons).
  const baselineCapabilities = useMemo(() => {
    const addOnIds = new Set(
      (joinDetails?.scope_add_ons ?? []).map((s) => s?.rfi_id).filter(Boolean)
    );
    return (joinDetails?.capabilities ?? []).filter(
      (c) => !addOnIds.has(c?.rfi_id)
    );
  }, [joinDetails?.capabilities, joinDetails?.scope_add_ons]);

  const baselineExcludedValues = useMemo(
    () => baselineCapabilities.map((c) => c?.rfi_id).filter(Boolean),
    [baselineCapabilities]
  );

  if (!joinDetails) return null;

  const requestedTags = isPending ? addOnTags : [];
  const hasRequestedAddOns = pendingAddOnServices.length > 0 || requestedTags.length > 0;
  const hasConfirmedAddOns = confirmedAddOnServices.length > 0 || addOnTags.length > 0;

  const requestBlock = hasRequestedAddOns && (
    <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-amber-800">
          Requested Add-On Scope
        </h3>
        {pendingAddOnServices.length > 0 && (
          <button
            type="button"
            className={CONFIRM_BUTTON_CLASS}
            disabled={isConfirming}
            onClick={onConfirm}
          >
            Confirm add-on scope
          </button>
        )}
      </div>
      <div className="mt-3">
        <AddOnBadges services={pendingAddOnServices} tags={requestedTags} />
      </div>
    </div>
  );

  if (isPending) {
    return (
      <>
        {requestBlock}
        <JoinRequestDetails joinDetails={joinDetails} />
      </>
    );
  }

  if (!isEditMode) {
    return (
      <>
        {requestBlock}
        {hasConfirmedAddOns && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-900">Scope Add-ons:</h3>
            <div className="mt-1">
              <AddOnBadges services={confirmedAddOnServices} tags={addOnTags} />
            </div>
          </div>
        )}
        <JoinRequestDetails joinDetails={joinDetails} />
      </>
    );
  }

  return (
    <EditScopeAddOns
      key={joinDetails.join_vqa_request_id}
      addOnServices={addOnServices}
      addOnTags={addOnTags}
      sponsorAdditionalTags={sponsorAdditionalTags}
      baselineCapabilities={baselineCapabilities}
      baselineExcludedValues={baselineExcludedValues}
      onSaveScope={onSaveScope}
      isSavingScope={isSavingScope}
      rfiList={rfiList}
      scopeSuggestions={scopeSuggestions}
    />
  );
}

// Mounted only once the join details are loaded, so the edit fields start from the saved values.
function EditScopeAddOns({
  addOnServices,
  addOnTags,
  sponsorAdditionalTags,
  baselineCapabilities,
  baselineExcludedValues,
  onSaveScope,
  isSavingScope,
  rfiList,
  scopeSuggestions,
}) {
  const [editAddOnServices, setEditAddOnServices] = useState(() =>
    addOnServices.map((s) => ({ value: s.rfi_id, label: s.rfi_name }))
  );
  const [editAddOnTags, setEditAddOnTags] = useState(addOnTags);
  const [editSponsorTags, setEditSponsorTags] = useState(sponsorAdditionalTags);

  const handleSave = () => {
    onSaveScope({
      addon_services: editAddOnServices.map((s) => ({
        rfi_id: s.value,
        rfi_name: s.label,
      })),
      addon_scope_tags: JSON.stringify(editAddOnTags),
      sponsor_additional_scope_tags: JSON.stringify(editSponsorTags),
    });
  };

  return (
    <div className="mt-4 space-y-4">
      <div>
        <h3 className="text-sm font-medium text-gray-900">
          Service Categories
        </h3>
        <div className="mt-1">
          <Capabilities services={baselineCapabilities} icon={true} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-900">Additional Scope</h3>
        <div className="mt-1">
          <AutocompleteTagInput
            tags={editSponsorTags}
            setTags={setEditSponsorTags}
            suggestions={scopeSuggestions}
            placeholder="Type custom scope tags and press Enter"
          />
        </div>
      </div>

      <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-amber-800">
            Scope Add-ons
          </h3>
          <button
            type="button"
            className={SAVE_BUTTON_CLASS}
            disabled={isSavingScope}
            onClick={handleSave}
          >
            Save Scope Add-ons
          </button>
        </div>
        <div className="mt-3">
          <ServiceCategoryV2Dropdown
            rfiList={rfiList}
            selected={editAddOnServices}
            onChange={setEditAddOnServices}
            excludedValues={baselineExcludedValues}
            placeholder="Select add-on service categories"
          />
        </div>
        <div className="mt-3">
          <AutocompleteTagInput
            tags={editAddOnTags}
            setTags={setEditAddOnTags}
            suggestions={scopeSuggestions}
            placeholder="Type custom add-on tags and press Enter"
          />
        </div>
      </div>
    </div>
  );
}
